"use client"

/**
 * Переключение на превью-сборку фикса (багфикс агент) и возврат на исходный стенд.
 *
 * Ссылка на сборку дополняется параметрами во фрагменте (он переживает серверные редиректы и не уходит на сервер):
 *   #casper_home=<origin стенда>&casper_build=<подпись>&casper_handoff=<base64url JSON сессии>
 * На сборке BugfixPreviewBridge принимает параметры, а плашка предлагает вернуться.
 * В мобильном приложении переключение запоминается нативно через CasperBridge.System.setStartUrl.
 */

import { getActiveAccount, saveTokens, type User } from "@/lib/auth"
import { casperBridgeSetStartUrl, isCasperBridgeAvailable } from "@/lib/casper-bridge"

export const PREVIEW_HOME_KEY = "bugfix-preview-home"
export const PREVIEW_LABEL_KEY = "bugfix-preview-label"
const HANDOFF_PARAM = "casper_handoff"
const HOME_PARAM = "casper_home"
const LABEL_PARAM = "casper_build"

interface Handoff {
  a: string
  r: string
  e: number
  u: User
}

const b64urlEncode = (s: string) => btoa(unescape(encodeURIComponent(s))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
const b64urlDecode = (s: string) => decodeURIComponent(escape(atob(s.replace(/-/g, "+").replace(/_/g, "/"))))

const fragment = (u: URL) => new URLSearchParams(u.hash.replace(/^#/, ""))

function setFragment(u: URL, params: URLSearchParams): void {
  const s = params.toString()
  u.hash = s ? `#${s}` : ""
}

export function previewLaunchUrl(target: string, opts: { label: string; withSession: boolean; home?: string }): string {
  const u = new URL(target)
  const params = fragment(u)
  params.set(HOME_PARAM, opts.home ?? window.location.origin)
  params.set(LABEL_PARAM, opts.label)
  const account = opts.withSession ? getActiveAccount() : null
  if (account?.tokens.accessToken) {
    const h: Handoff = { a: account.tokens.accessToken, r: account.tokens.refreshToken, e: account.tokens.expiresAt, u: account.user }
    params.set(HANDOFF_PARAM, b64urlEncode(JSON.stringify(h)))
  }
  setFragment(u, params)
  return u.toString()
}

/** Ссылка без сессии (для setStartUrl — токены не должны храниться в настройках приложения) */
export function stripHandoff(url: string): string {
  const u = new URL(url)
  const params = fragment(u)
  params.delete(HANDOFF_PARAM)
  setFragment(u, params)
  return u.toString()
}

/**
 * Принять параметры сборки из адреса. Сессию принимаем, только если вход не выполнен
 * или это тот же пользователь (обновление токена) — чужая ссылка не подменит аккаунт.
 */
export function consumePreviewParams(): void {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  const params = fragment(url)
  if (!params.has(HOME_PARAM) && !params.has(HANDOFF_PARAM)) return
  const home = params.get(HOME_PARAM)
  if (home) {
    try {
      const origin = new URL(home).origin
      if (origin !== window.location.origin) {
        localStorage.setItem(PREVIEW_HOME_KEY, origin)
        localStorage.setItem(PREVIEW_LABEL_KEY, params.get(LABEL_PARAM) || "")
      }
    } catch {
      // некорректный адрес
    }
  }
  const raw = params.get(HANDOFF_PARAM)
  if (raw) {
    try {
      const h = JSON.parse(b64urlDecode(raw)) as Handoff
      const active = getActiveAccount()
      const fresh = h.e > Date.now() + 30_000
      if (fresh && h.a && h.u?.id && (!active || active.user.id === h.u.id)) {
        saveTokens({ accessToken: h.a, refreshToken: h.r, expiresIn: Math.floor((h.e - Date.now()) / 1000) }, h.u)
      }
    } catch {
      // битая сессия — пользователь войдёт сам
    }
  }
  for (const k of [HOME_PARAM, LABEL_PARAM, HANDOFF_PARAM]) params.delete(k)
  setFragment(url, params)
  window.history.replaceState(window.history.state, "", url.toString())
}

export function previewHome(): { home: string; label: string } | null {
  if (typeof window === "undefined") return null
  const home = localStorage.getItem(PREVIEW_HOME_KEY)
  if (!home || home === window.location.origin) return null
  return { home, label: localStorage.getItem(PREVIEW_LABEL_KEY) || "" }
}

export function forgetPreviewHome(): void {
  localStorage.removeItem(PREVIEW_HOME_KEY)
  localStorage.removeItem(PREVIEW_LABEL_KEY)
}

/** Открыть сборку в мобильном приложении: нативный start URL + переход */
export async function switchMobileToBuild(mobileUrl: string, label: string, withSession: boolean, home?: string): Promise<void> {
  const launch = previewLaunchUrl(mobileUrl, { label, withSession, home })
  if (isCasperBridgeAvailable()) {
    try {
      await casperBridgeSetStartUrl(stripHandoff(launch))
    } catch (e) {
      console.warn("[bugfix-preview] setStartUrl:", e)
    }
  }
  window.location.replace(launch)
}

/** Вернуться на исходный стенд (dev) */
export async function returnToHome(home: string, mobile: boolean): Promise<void> {
  const target = `${home.replace(/\/+$/, "")}${mobile ? "/mobile" : ""}`
  if (mobile && isCasperBridgeAvailable()) {
    try {
      await casperBridgeSetStartUrl(target)
    } catch (e) {
      console.warn("[bugfix-preview] setStartUrl:", e)
    }
  }
  forgetPreviewHome()
  window.location.replace(target)
}
